import { useIntervalFn } from '@vueuse/core';
import isEqual from 'lodash/isEqual';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { deepCompareComputed } from '@/composable/deepCompareComputed';
import { useIdle } from '@/composable/useIdle';
import { useState } from '@/composable/useState';
import BusinessLogicHelper from '@/core/lib/businessLogicHelper';
import { deepFreeze } from '@/core/lib/utils';
import { isWsConnected } from '@/core/oddsApi/apollo';
import { formatEventEuro } from '@/core/oddsApi/helpers/formatEvent';
import type { EnumOddsCategoryType } from '@/core/oddsApi/oddsApiType';
import { useEventEuroQuery, useEventEuroSubscription } from '@/core/oddsApi/oddsApiType';
import type { IEventEuro } from '@/interface/IEvent';
import { useOddsApiStore } from '@/store/oddsApiStore';

export function useEventEuro(
    eventId: number,
    oddsCategory: EnumOddsCategoryType,
    isActive = ref(true),
) {
    const variables = computed(() => ({
        query: {
            id: eventId,
            oddsCategory,
        },
    }));

    const [isEnabled, setIsEnabled] = useState(true);
    const refetch = async () => {
        if (isEnabled.value) {
            setIsEnabled(false);
            nextTick(() => setIsEnabled(true));
        }
    };

    const { loaded, loading, onResult } = useEventEuroQuery(
        variables,
        deepCompareComputed(() => ({
            enabled: isActive.value && isEnabled.value,
        })),
    );

    const { onSubscription } = useEventEuroSubscription(
        variables,
        deepCompareComputed(() => ({
            enabled: isActive.value && isEnabled.value,
        })),
    );

    const event = ref<IEventEuro | null>(null);
    const { customerSettings, additionalSettings } = storeToRefs(useOddsApiStore());

    onResult((result) => {
        if (result) {
            if (BusinessLogicHelper.isEventAllowed(customerSettings.value, result, additionalSettings.value)) {
                const newEvent = deepFreeze(formatEventEuro(result));
                if (!isEqual(event.value, newEvent)) {
                    event.value = newEvent;
                }
            } else {
                event.value = null;
            }
        }
    });

    onSubscription((subscription) => {
        if (subscription && BusinessLogicHelper.isEventAllowed(customerSettings.value, subscription, additionalSettings.value)) {
            const newEvent = deepFreeze(formatEventEuro(subscription));

            if (!isEqual(event.value, newEvent)) {
                event.value = newEvent;
            }
        } else {
            event.value = null;
        }
    });

    const refreshInterval = computed(() => (event.value?.isLive ? 20 : 60) * 1000);

    useIntervalFn(() => {
        if (!isEnabled.value || isWsConnected.value) return;
        refetch();
    }, refreshInterval);

    // Automatically recover from idle state with a force refresh
    // This is to prevent the user from losing data when they are idle
    const { onActive } = useIdle(60 * 1000);
    onActive(() => {
        // eslint-disable-next-line no-console
        console.log('useEventEuroQuery: Recovering from idle state...');
        refetch();
    });

    watch(variables, () => {
        loaded.value = false;
        event.value = null;
    });

    return {
        loaded,
        loading,
        refetch,
        event,
    };
}
